import { STARTING_CASH } from '../config';

export interface Stock {
  symbol: string;
  name: string;
  sector: string;
  basePrice: number;
}

export interface Quote extends Stock {
  price: number;
  change: number;
  changePct: number;
}

export interface Position {
  symbol: string;
  shares: number;
  avgCost: number;
}

export interface Trade {
  id: string;
  symbol: string;
  side: 'buy' | 'sell';
  shares: number;
  price: number;
  total: number;
  timestamp: string;
}

export interface Portfolio {
  cash: number;
  positions: Position[];
  trades: Trade[];
}

export const STOCKS: Stock[] = [
  { symbol: 'AAPL', name: 'Apple Inc.', sector: 'Technology', basePrice: 182 },
  { symbol: 'MSFT', name: 'Microsoft Corp.', sector: 'Technology', basePrice: 415 },
  { symbol: 'GOOGL', name: 'Alphabet Inc.', sector: 'Technology', basePrice: 175 },
  { symbol: 'AMZN', name: 'Amazon.com Inc.', sector: 'Consumer', basePrice: 198 },
  { symbol: 'NVDA', name: 'NVIDIA Corp.', sector: 'Technology', basePrice: 875 },
  { symbol: 'META', name: 'Meta Platforms', sector: 'Technology', basePrice: 530 },
  { symbol: 'TSLA', name: 'Tesla Inc.', sector: 'Automotive', basePrice: 175 },
  { symbol: 'JPM', name: 'JPMorgan Chase', sector: 'Finance', basePrice: 210 },
  { symbol: 'JNJ', name: 'Johnson & Johnson', sector: 'Healthcare', basePrice: 148 },
  { symbol: 'V', name: 'Visa Inc.', sector: 'Finance', basePrice: 278 },
  { symbol: 'WMT', name: 'Walmart Inc.', sector: 'Consumer', basePrice: 68 },
  { symbol: 'UNH', name: 'UnitedHealth Group', sector: 'Healthcare', basePrice: 530 },
  { symbol: 'XOM', name: 'Exxon Mobil', sector: 'Energy', basePrice: 112 },
  { symbol: 'PG', name: 'Procter & Gamble', sector: 'Consumer', basePrice: 165 },
  { symbol: 'HD', name: 'Home Depot', sector: 'Retail', basePrice: 388 },
  { symbol: 'BAC', name: 'Bank of America', sector: 'Finance', basePrice: 40 },
  { symbol: 'MA', name: 'Mastercard Inc.', sector: 'Finance', basePrice: 476 },
  { symbol: 'NFLX', name: 'Netflix Inc.', sector: 'Media', basePrice: 640 },
  { symbol: 'DIS', name: 'Walt Disney Co.', sector: 'Media', basePrice: 98 },
  { symbol: 'KO', name: 'Coca-Cola Co.', sector: 'Consumer', basePrice: 62 },
];

// Seeded random walk — price changes per symbol per session tick
const _offsets: Record<string, number> = {};

export function getQuotes(): Quote[] {
  return STOCKS.map(s => {
    if (!_offsets[s.symbol]) _offsets[s.symbol] = (Math.random() - 0.5) * 0.06;
    const pct = _offsets[s.symbol];
    const price = Math.max(1, s.basePrice * (1 + pct));
    return {
      ...s,
      price: +price.toFixed(2),
      change: +(price - s.basePrice).toFixed(2),
      changePct: +(pct * 100).toFixed(2),
    };
  });
}

export function tickPrices() {
  for (const s of STOCKS) {
    const drift = (Math.random() - 0.499) * 0.008;
    _offsets[s.symbol] = (_offsets[s.symbol] ?? 0) + drift;
  }
}

export function getQuote(symbol: string): Quote | undefined {
  return getQuotes().find(q => q.symbol === symbol);
}

const STORAGE_KEY = 'trading_portfolio';

export function loadPortfolio(): Portfolio {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as Portfolio;
  } catch { /* ignore */ }
  return { cash: STARTING_CASH, positions: [], trades: [] };
}

export function savePortfolio(p: Portfolio) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
}

export function resetPortfolio() {
  localStorage.removeItem(STORAGE_KEY);
}
